import {gsap, TimelineLite} from "gsap";
import {MathHelper} from "../../../../core/utils/MathHelper";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {WalkOfShameGameAssets} from "../../../WalkOfShameGameAssets";
import {Layers, WalkOfShameLayers} from "../../../view/WalkOfShameLayers";
import {WalkOfShame} from "../../../WalkOfShame";
import {WalkOfShameSound} from "../../../sound/WalkOfShameSound";
import {IWinRatioConfig} from "../../../../core/config/WinRatio";

interface IEndSoundNVolume {
    sound: string;
    volume: number;
}

export class InitialWinCountUp extends PIXI.Container {
    private readonly _countUp: BitmapCountUp;
    private _soundPlayedOnce: boolean = false;

    constructor() {
        super();

        const style = {
            fontName: WalkOfShameGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 36,
            tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
        };
        this._countUp = new BitmapCountUp(style);
        this._countUp.shrinkSize = true;
        this._countUp.visible = false;
        this.addChild(this._countUp);
        this.position.set(360);
        WalkOfShameLayers.getLayer(Layers.WIN_COUNT_UP)?.addChild(this);
    }

    private _isAborted: boolean = false;

    set isAborted(value: boolean) {
        this._isAborted = value;
    }

    private static getScale(multiplier: number): number {
        return 1 + (multiplier - 1) * 0.1;
    }

    public getCountUpAnimation(from: number, to: number, duration: number, multiplier: number): TimelineLite {
        const baseToNumber: number = (multiplier === 1 ? to : (to / multiplier)),
            tl: TimelineLite = new TimelineLite(),
            endSoundNVolume: IEndSoundNVolume = this.setEndSoundNVolume();

        // #152
        this._isAborted = false;
        this._soundPlayedOnce = false;

        tl.add([
            () => {
                this._countUp.maxTextWidth = (600 / InitialWinCountUp.getScale(multiplier));
                this._countUp.scale.set(1);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                if (duration > 0.01) {
                    WalkOfShameSound.soundPlayer.playTrack({
                        name: WalkOfShameSound.COUNT_UP,
                        repeat: Number.MAX_VALUE
                    });
                }
            },
            this._countUp.getCountUpAnimation(from, baseToNumber, duration, `power3.inOut`)
        ]);

        tl.add(() => {
            WalkOfShameSound.soundPlayer.stopTrack(WalkOfShameSound.COUNT_UP);
        });

        if (multiplier > 1) {
            const levelTime: number = ((0.5 * multiplier > 5) ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5);
            for (let m: number = 1; m <= multiplier; m++) {
                const showNumber: number = baseToNumber * m,
                    scale: number = InitialWinCountUp.getScale(m);

                tl.add([
                    () => {
                        if (!this._isAborted || !this._soundPlayedOnce) {
                            WalkOfShameSound.soundPlayer.playTrack({
                                name: WalkOfShameSound.MULTIPLIER_COUNT_UP.next(),
                                volume: endSoundNVolume.volume
                            });

                            this._soundPlayedOnce = true;
                        }
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, 0.01),
                    gsap.to(this._countUp.scale,
                        {
                            duration: levelTime / 2,
                            x: scale,
                            y: scale,
                            ease: `elastic.out(2, 1)`
                        }),
                    gsap.to(this, {duration: levelTime}) // a pause for half levelTime
                ]);
            }
        }

        tl.add(() => {
            WalkOfShameSound.soundPlayer.playTrack({
                name: endSoundNVolume.sound
            });

            WalkOfShame.winWays.showWinWays(WalkOfShame.model.parsedGameData.totalWaysWon, (multiplier > 1 ? this.y + (this._countUp.height * 0.85) : 455));
        });
        tl.add(() => {
            this.remove();
        }, "+=1");

        return tl;
    }

    private setEndSoundNVolume(): IEndSoundNVolume {
        const winRatio: IWinRatioConfig | null = WalkOfShame.model.parsedGameData.currentWinRatioConfig;
        let retVal: IEndSoundNVolume = {
            sound: "",
            volume: 0
        };

        if (winRatio) {
            if (winRatio.name === "Tiny Win") {
                retVal = {
                    sound: WalkOfShameSound.COUNT_UP_END_TINY,
                    volume: 0.4
                };
            } else if (winRatio.name === "Small Win") {
                retVal = {
                    sound: WalkOfShameSound.COUNT_UP_END_SMALL,
                    volume: 0.5
                };
            } else if (winRatio.name === "Medium Win") {
                retVal = {
                    sound: WalkOfShameSound.COUNT_UP_END_MEDIUM,
                    volume: 0.8
                };
            } else if (winRatio.name === "Large Win") {
                retVal = {
                    sound: WalkOfShameSound.COUNT_UP_END_LARGE,
                    volume: 1
                };
            }
        }

        return retVal;
    }

    private remove(): void {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                this._countUp.visible = false;
            }
        });
        if (this._countUp.visible) {
            tl.to(this._countUp, {
                duration: 0.3,
                alpha: 0
            }, 0);
            tl.add(() => {
                WalkOfShame.winWays.hideInitialWinWays();
            }, 0);
        }
    }
}